import React, { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { InventoryEntities } from 'client/data/models/inventory';

import { Link } from 'site-modules/shared/components/link/link';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { DealerReviewsOverallRatingWrapper } from 'site-modules/shared/components/dealer-reviews/dealer-reviews-overall-rating-wrapper';
import { TelephoneLink } from 'client/site-modules/shared/components/telephone-link/telephone-link';
import { DirectionLink } from 'site-modules/shared/components/inventory/direction-link/direction-link';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getDealershipStateCityNameUrl } from 'site-modules/shared/utils/dealership-link-constructor';

import { toDisplayFormattedAddress } from 'site-modules/shared/utils/address-format';
import { createNicePhone } from 'site-modules/shared/utils/phone-format';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { makeNiceName } from 'client/site-modules/shared/utils/nice-name';
import { CTA_TEXT } from 'site-modules/shared/constants/inventory/lead-form';
import { REVIEWS_SALES } from 'client/constants/dealer-reviews';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';

import './dealer-info-content.scss';

export const DealerInfoContent = ({
  vinData,
  pageNameLegacy = '',
  dealerWebSiteUrl = '',
  url = '',
  isCarZingPartner = false,
}) => {
  const isPartnerView = isPartnerDealer(vinData);
  const { address, name: dealerName } = vinData.dealerInfo;
  const type = vinData.type || '';
  const franchiseId = vinData.dealerInfo.franchiseId;
  const dealerLocationId = vinData.dealerInfo.rooftopId;
  const rooftopLogicalName = vinData.dealerInfo.rooftopLogicalName;
  const phoneNumber = vinData.computedDisplayInfo?.phoneNumber ?? '';
  const formattedPhone = createNicePhone(phoneNumber);
  const make = vinData.vehicleInfo?.styleInfo?.make ?? '';
  const niceMake = makeNiceName(make);
  const fiveStarYears = vinData.dealerInfo.displayInfo?.fiveStarYears ?? [];
  const isFiveStarDealer = !!fiveStarYears.length;
  const fiveStarTitle = 'Five Star Dealer';
  const isNewVin = isNew(type);
  const dataTrackingParent = `${isNewVin ? 'new' : 'used'}-dealer_info_phonenum-${pageNameLegacy}`;
  const logoPath = getStaticImageUrl(`/img/oem-logos/75x75/${niceMake}.png`);
  const iconRules = 'text-white';
  const partnerCode = vinData?.dealerInfo?.partnerCode;
  const dealerSalesReviewLink = getDealershipStateCityNameUrl({
    state: address.stateName,
    city: address.city,
    dealership: rooftopLogicalName,
    params: { type: REVIEWS_SALES },
  });
  const dealershipLink = getDealershipStateCityNameUrl({
    state: address.stateName,
    city: address.city,
    dealership: rooftopLogicalName,
  });

  return (
    <ContentSection
      dataTrackingParent={dataTrackingParent}
      className="dealer-info"
      line={false}
      titleClassName="section-title heading-4 mb-1_5"
    >
      <div className={classNames('d-flex justify-content-between align-items-top', { 'mb-1_5': isPartnerView })}>
        <div>
          {isFiveStarDealer && (
            <div className="text-primary-darker mb-0_5">
              <span title={fiveStarTitle} className="icon-5star me-0_25" aria-hidden />
              <span className="fw-medium">{fiveStarTitle}</span>
            </div>
          )}
          <div data-test="dealer-info">
            <h3 className="heading-4 mb-0_5 dealer-name">{dealerName}</h3>
            <div className="dealer-address text-gray-40">{toDisplayFormattedAddress(address)}</div>
          </div>
        </div>
        <div>
          {isNewVin && make && (
            <img src={logoPath} className="ms-1 mt-0_25" title={`${make} Logo`} alt={`${make} Logo`} loading="lazy" />
          )}
        </div>
      </div>
      {isPartnerView && (
        <Fragment>
          <DealerReviewsOverallRatingWrapper vinData={vinData} dealerReviewLink={dealerSalesReviewLink} />
          <div className="text-cool-gray-10 mx-0 mb-1_5 fw-medium contact-items">
            <TelephoneLink
              linkClassName="text-decoration-none-hover m-0 contact-item p-1"
              iconClassNames={classNames(iconRules)}
              iconWrapperClasses="bg-blue-50"
              formattedPhone={formattedPhone}
              dealerInfo={{
                dataTrackingId: 'vdp_call_dealer',
                dataDealerFranchiseId: franchiseId,
                dataDealerRooftopId: dealerLocationId,
              }}
              telephoneLinkCTAName={CTA_TEXT.CALL_DEALER}
              isRedesign
            />
            <DirectionLink
              dealerInfo={{
                address,
                name: dealerName,
              }}
              classes="m-0 contact-item p-1"
              iconClasses={classNames(iconRules)}
              iconWrapperClasses="bg-blue-50"
              isRedesign
            />
            <Link
              to={srpLinkBuilder({ dealerlocationid: dealerLocationId })}
              className="text-decoration-none-hover m-0 contact-item p-1"
              data-tracking-id="vdp_view_inventory"
            >
              <div className="circle-btn bg-blue-50 rounded-circle d-inline-flex justify-content-center align-items-center me-0_75">
                <i className={classNames('icon-price-tags2 me-0_25', iconRules)} aria-hidden />
              </div>
              Inventory
            </Link>
            {!!dealerWebSiteUrl && (
              <Link
                to={url}
                target="_blank"
                className="text-decoration-none-hover m-0 contact-item p-1"
                data-tracking-id="dealer_site_clickout"
                data-dealer-franchise-id={franchiseId}
                data-dealer-rooftop-id={dealerLocationId}
                data-tracking-value={dealerWebSiteUrl}
                {...(isCarZingPartner
                  ? {
                      'data-tracking-parent': `${dataTrackingParent}-${partnerCode?.includes('TRIAL') ? 'trial' : 'paid'}_carzing`,
                    }
                  : {})}
              >
                <div className="circle-btn bg-blue-50 rounded-circle d-inline-flex justify-content-center align-items-center me-0_75">
                  <i className={classNames('icon-open_in_new', iconRules)} aria-hidden />
                </div>
                {`Dealer's Website`}
              </Link>
            )}
          </div>
          <Link
            to={dealershipLink}
            data-tracking-id="vdp_view_dealer_details"
            data-dealer-franchise-id={franchiseId}
            data-dealer-rooftop-id={dealerLocationId}
            className="text-primary-darker py-0_25"
          >
            Learn more about this dealer
            <span title={fiveStarTitle} className="icon-arrow-right3 ms-0_25 size-12" aria-hidden />
          </Link>
        </Fragment>
      )}
    </ContentSection>
  );
};
DealerInfoContent.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  pageNameLegacy: PropTypes.string,
  dealerWebSiteUrl: PropTypes.string,
  url: PropTypes.string,
  isCarZingPartner: PropTypes.bool,
};
